import "./EntityUpdate.scss";
import React, { useState } from "react";
import {
  Col,
  Row,
  Input,
  message,
  Button,
  Select,
} from "antd";
import { ArrowRightOutlined, LoadingOutlined } from "@ant-design/icons";
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import JobDetails from "./JobDetails";

const { Option } = Select;

const SceneActionForm = () => {
  const [sceneId, setSceneId] = useState("");
  const [cameraName, setCameraName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [sceneJobData, setSceneJobData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [sceneData, setSceneData] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraImage, setSelectedCameraImage] = useState(null);

  const handleSceneIdChange = (e) => {
    setSceneId(e.target.value);
    if (hasError && e.target.value) {
      setHasError(false);
    }
    // Reset camera data when scene ID changes
    setCameraName("");
    setAvailableCameras([]);
    setSelectedCameraImage(null);
    setSceneData(null);
    setIsDataLoaded(false);
    setSceneJobData(null);
  };

  const handleCameraNameChange = (value) => {
    setCameraName(value);
    handleFetchJob(value);
    if (hasError && value) {
      setHasError(false);
    }
    
    // Set the selected camera image
    if (value && sceneData) {
      const selectedCamera = availableCameras.find(camera => camera.filename === value);
      if (selectedCamera) {
        const imageUrl = ENVIRONMENT.getBaseURL(sceneData.platform) + 
                        ENVIRONMENT.SCENE_THUMBNAIL_URI + 
                        sceneId + '/' + 
                        getFilenameWithoutExtension(selectedCamera.filename) + '_512.jpg';
        setSelectedCameraImage(imageUrl);
      }
    } else {
      setSelectedCameraImage(null);
    }
  };

  const fetchSceneData = () => {
    if (!sceneId) {
      setHasError(true);
      message.error("Scene ID cannot be empty.");
      return;
    }

    setIsLoading(true);
    setIsDataLoaded(false);
    setAvailableCameras([]);
    setSelectedCameraImage(null);
    setSceneData(null);
    setSceneJobData(null);

    const payload = {
      scene_id: sceneId,
    };

    // Get scene details first
    axios
      .post(ENVIRONMENT.GET_SCENE_DETAIL, payload)
      .then((res) => {
        if (res.data && Object.keys(res.data).length > 0) {
          const scene = res.data;
          setSceneData(scene);
          
          // Parse final_renders and filter for 'rendered' or 'pending' status
          let cameras = [];
          if (scene.final_renders) {
            try {
              const finalRendersObj = JSON.parse(scene.final_renders);
              if (finalRendersObj.final_renders && Array.isArray(finalRendersObj.final_renders)) {
                cameras = finalRendersObj.final_renders.filter(
                  render => render.status === 'rendered' || render.status === 'pending'
                );
              }
            } catch (error) {
              console.error("Error parsing final_renders:", error);
            }
          }
          
          setAvailableCameras(cameras);
          
          if (cameras.length === 0) {
            message.warning("No rendered or pending cameras found for this scene.");
          } else {
            message.info(`Found ${cameras.length} available cameras for this scene.`);
          }
          
          setIsLoading(false);
        } else {
          message.error("Scene not found");
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching scene data:", error);
        message.error("Error in fetching scene data");
        setIsLoading(false);
      });
  };

  const handleFetchJob = (cameraName) => {  
    if (!sceneId || !cameraName) {
      setHasError(true);
      message.error("Scene ID and Camera Name cannot be empty.");
      return;
    }

    setIsLoading(true);
    setIsDataLoaded(false);

    const payload = {
      scene_id: sceneId,
      camera_name: getFilenameWithoutExtension(cameraName),
    };

    // Get scene configuration data
    axios
      .post(ENVIRONMENT.AUTOMATION_GET_BY_SCENE_CAMERA, payload)
      .then((res) => {
        if (res.data && Object.keys(res.data).length > 0) {
          // Check if there's job-related data in the response
          const sceneData = res.data;
          
          // Create job data structure based on available information
          const jobData = {
            request_id: sceneData.request_id || `scene_${sceneId}_${cameraName}`,
            type: sceneData.job_type || "scene_renders",
            request_type: sceneData.job_type || "scene_renders",
            status: sceneData.status || "completed",
            entity_id: sceneId,
            entity_type: "scene",
            error_message: sceneData.error_message || "",
            customer_username: sceneData.customer_username || "",
            end_time: sceneData.end_time || sceneData.last_modified || "",
            // Add placement info if available
            placement_info: sceneData.placement_info,
            room_id: sceneData.room_id,
          };
          
          setSceneJobData(jobData);
          setIsDataLoaded(true);
          message.info("Scene job data fetched successfully");
        } else {
          message.error("Scene configuration for snapshot does not exist");
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching scene job data:", error);
        message.error("Error in fetching scene job data");
        setIsLoading(false);
      });
  };

  // Helper function to get filename without extension
  const getFilenameWithoutExtension = (filename) => {
    return filename ? filename.split('.').slice(0, -1).join('.') : '';
  };

  return (
    <div className="scene-action-form">
      <div style={{ padding: "20px" }}>
        <Row gutter={16}>
          <Col span={8}>
            <div className="scene-input-container">
              <label>Scene ID</label>
              <div className="input-container">
                <Input
                  placeholder="Please enter Scene ID"
                  type="number"
                  value={sceneId}
                  onChange={handleSceneIdChange}
                  className={`custom-input-id ${hasError ? "input-error" : ""}`}
                  onPressEnter={fetchSceneData}
                  suffix={
                    <div className="suffix-container" onClick={fetchSceneData}>
                      {isLoading ? (
                        <LoadingOutlined className="custom-icon" />
                      ) : (
                        <ArrowRightOutlined className="custom-icon" />
                      )}
                    </div>
                  }
                />
              </div>
            </div>
            {sceneData && (
              <div className="scene-input-container">
                <label>Camera Name</label>
                <div className="input-container">
                  <Select
                    placeholder="Please select Camera Name"
                    value={cameraName}
                    onChange={handleCameraNameChange}
                    className="camera-select"
                    showSearch
                  >
                    {availableCameras.map((camera) => (
                      <Option key={camera.filename} value={camera.filename}>
                        {camera.display_name || getFilenameWithoutExtension(camera.filename)} ({camera.status})
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
                      {isDataLoaded && sceneJobData ? (
            <div>
              <div className="job-details-container">
                <label>Scene ID</label>
                <div className="manrope f-14 job-details-data">
                  <span>{sceneJobData.entity_id}</span>
                </div>
              </div>
              <div className="job-details-container">
                <label>Camera Name</label>
                <div className="manrope f-14 job-details-data">
                  <span>{cameraName}</span>
                </div>
              </div>
              {sceneJobData.room_id && (
                <div className="job-details-container">
                  <label>Room ID</label>
                  <div className="manrope f-14 job-details-data">
                    <span>{sceneJobData.room_id}</span>
                  </div>
                </div>
              )}
              {sceneJobData.customer_username && (
                <div className="job-details-container">
                  <label>Customer Username</label>
                  <div className="manrope f-14 job-details-data">
                    <span>{sceneJobData.customer_username}</span>
                  </div>
                </div>
              )}
              <div className="job-details-container">
                <label>Job Type</label>
                <div className="manrope f-14 job-details-data">
                  <span>{sceneJobData.type}</span>
                </div>
              </div>
              <div className="job-details-container">
                <label>Status</label>
                <div className="manrope f-14 job-details-data">
                  <span>{sceneJobData.status}</span>
                </div>
              </div>
              {sceneJobData.end_time && (
                <div className="job-details-container">
                  <label>Last Modified</label>
                  <div className="manrope f-14 job-details-data">
                    <span>{new Date(sceneJobData.end_time).toLocaleString()}</span>
                  </div>
                </div>
              )}
              {sceneJobData.error_message && (
                <div className="job-details-container">
                  <label>Error Message</label>
                  <div className="manrope f-14 job-details-data">
                    <span className="error-message">{sceneJobData.error_message}</span>
                  </div>
                </div>
              )}
              {isDataLoaded && sceneJobData && (
              <div className="scene-input-container">
                <Button
                  type="primary"
                  onClick={() => setModalOpen(true)}
                  className="modal-okay square font-12"
                >
                  Regenerate Job
                </Button>
              </div>
            )}
            </div>
          ) : (
            ""
          )}
          </Col>
          {selectedCameraImage && (
            <Col span={8} offset={4}>
              <div className="camera-preview-container">
                <label>Camera Preview</label>
                <div>
                  <img
                    src={selectedCameraImage}
                    alt="Camera Preview"
                    className="camera-preview-image"
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                  />
                </div>
              </div>
            </Col>
          )}
        </Row>

        <JobDetails
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          selectedJob={sceneJobData}
        />
      </div>
    </div>
  );
};

export default SceneActionForm; 